import { useFloating, autoUpdate, offset, shift, flip } from "@floating-ui/react";

interface HighlightConfirmationPopupProps {
  isOpen: boolean;
  onSave: () => void;
  onCancel: () => void;
  onNavigateUp: () => void;
  onNavigateDown: () => void;
  referenceElement: HTMLElement | null;
  canNavigateUp?: boolean;
  canNavigateDown?: boolean;
  popupPlacement?: "top" | "bottom";
}

export const HighlightConfirmationPopup = ({
  isOpen,
  onSave,
  onCancel,
  onNavigateUp,
  onNavigateDown,
  referenceElement,
  canNavigateUp,
  canNavigateDown,
  popupPlacement = "top",
}: HighlightConfirmationPopupProps) => {
  const { refs, floatingStyles } = useFloating({
    elements: {
      reference: referenceElement,
    },
    placement: popupPlacement,
    middleware: [offset(10), flip(), shift()],
    whileElementsMounted: autoUpdate,
  });

  if (!isOpen) return null;

  return (
    <div
      ref={refs.setFloating}
      style={{
        ...floatingStyles,
        zIndex: 2147483647,
        display: "flex",
        gap: "0.5rem",
        backgroundColor: "white",
        borderRadius: "0.375rem",
        boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
        padding: "0.5rem",
        border: "1px solid #e5e7eb",
        pointerEvents: "auto",
      }}
      onPointerDown={(e) => e.stopPropagation()}
    >
      <div
        style={{
          backgroundColor: "rgba(79,70,229,1)",
          color: "white",
          padding: "0.5rem 1rem",
          borderRadius: "0.25rem",
          border: "none",
          cursor: "pointer",
          height: "auto",
          width: "auto",
          fontSize: "12px",
        }}
        onClick={onSave}
        onMouseOver={(e) => (e.currentTarget.style.backgroundColor = "#4338ca")}
        onMouseOut={(e) => (e.currentTarget.style.backgroundColor = "rgba(79,70,229,1)")}
      >
        Pick
      </div>
      <div
        style={{
          backgroundColor: "#6c757d",
          color: "white",
          padding: "0.5rem 1rem",
          borderRadius: "0.25rem",
          border: "none",
          cursor: "pointer",
          height: "auto",
          width: "auto",
          fontSize: "12px",
        }}
        onClick={onCancel}
        onMouseOver={(e) => (e.currentTarget.style.backgroundColor = "#545b62")}
        onMouseOut={(e) => (e.currentTarget.style.backgroundColor = "#6c757d")}
      >
        Cancel
      </div>
      {canNavigateUp && (
        <div
          // role="button"
          aria-disabled={!canNavigateUp}
          // tabIndex={canNavigateUp ? 0 : -1}
          title="Select Parent Element"
          style={{
            backgroundColor: "#f0f0f0",
            color: "#333",
            padding: "0.5rem",
            borderRadius: "0.25rem",
            border: "1px solid #ccc",
            cursor: canNavigateUp ? "pointer" : "default",
            height: "auto",
            width: "auto",
            fontSize: "12px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            opacity: canNavigateUp ? 1 : 0.5,
          }}
          onClick={canNavigateUp ? onNavigateUp : undefined}
          onMouseOver={(e) => canNavigateUp && (e.currentTarget.style.backgroundColor = "#e0e0e0")}
          onMouseOut={(e) => canNavigateUp && (e.currentTarget.style.backgroundColor = "#f0f0f0")}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
            <path
              fillRule="evenodd"
              d="M8 15a.5.5 0 0 0 .5-.5V2.707l3.146 3.147a.5.5 0 0 0 .708-.708l-4-4a.5.5 0 0 0-.708 0l-4 4a.5.5 0 1 0 .708.708L7.5 2.707V14.5a.5.5 0 0 0 .5.5z"
            />
          </svg>
        </div>
      )}
      {canNavigateDown && (
        <div
          aria-disabled={!canNavigateDown}
          title="Select First Child Element"
          style={{
            backgroundColor: "#f0f0f0",
            color: "#333",
            padding: "0.5rem",
            borderRadius: "0.25rem",
            border: "1px solid #ccc",
            cursor: canNavigateDown ? "pointer" : "default",
            height: "auto",
            width: "auto",
            fontSize: "12px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            opacity: canNavigateDown ? 1 : 0.5,
          }}
          onClick={canNavigateDown ? onNavigateDown : undefined}
          onMouseOver={(e) => canNavigateDown && (e.currentTarget.style.backgroundColor = "#e0e0e0")}
          onMouseOut={(e) => canNavigateDown && (e.currentTarget.style.backgroundColor = "#f0f0f0")}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
            <path
              fillRule="evenodd"
              d="M8 1a.5.5 0 0 1 .5.5v11.793l3.146-3.147a.5.5 0 0 1 .708.708l-4 4a.5.5 0 0 1-.708 0l-4-4a.5.5 0 0 1 .708-.708L7.5 13.293V1.5A.5.5 0 0 1 8 1z"
            />
          </svg>
        </div>
      )}
    </div>
  );
};
